import React, { useState } from 'react';
import { 
  ArrowUpIcon, 
  ArrowDownIcon, 
  CurrencyDollarIcon,
  ChartBarIcon,
  WalletIcon
} from '@heroicons/react/24/outline';

const PositionStatus: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  
  // 호버 상태 관리
  const [isBuyHovered, setIsBuyHovered] = useState(false);
  const [isSellHovered, setIsSellHovered] = useState(false);

  // Mock 포지션 데이터
  const position = {
    symbol: 'BTCUSDT',
    side: 'LONG',
    size: 0.015,
    currentPrice: 105000,
    averagePrice: 103500,
    pnl: 22.50,
    pnlPercent: 1.45,
    usdtValue: 1575.00,
    isOpen: true
  };

  const handleBuy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🔵 매수 버튼 클릭됨');
    setIsLoading(true);
    
    try {
      console.log('📤 매수 요청 전송 중...');
      await executeTradeAction('buy');
      alert('매수 주문이 성공적으로 접수되었습니다!');
      
    } catch (error) {
      console.error('❌ 매수 오류:', error);
      alert('매수 주문에 실패했습니다. 콘솔에서 세부 정보를 확인하세요.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSell = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🔴 매도 버튼 클릭됨');
    setIsLoading(true);
    
    try {
      console.log('📤 매도 요청 전송 중...');
      await executeTradeAction('sell');
      alert('매도 주문이 성공적으로 접수되었습니다!');
      
    } catch (error) {
      console.error('❌ 매도 오류:', error);
      alert('매도 주문에 실패했습니다. 콘솔에서 세부 정보를 확인하세요.');
    } finally {
      setIsLoading(false);
    }
  };

  const isProfitable = position.pnl >= 0;

  // 로컬 포맷팅 함수들
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const executeTradeAction = (action: string) => {
    console.log('Trade action:', action);
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 2000);
  };

  return (
    <div className="space-y-4">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <ChartBarIcon className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
          현재 포지션
        </h3>
        </div>
        <span className="px-3 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 rounded-full text-xs font-medium">
          {position.symbol}
        </span>
      </div>

      {/* 현재 가격 카드 */}
      <div className="bg-white dark:bg-dark-800 rounded-xl border border-slate-200 dark:border-dark-600 p-4">
          <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
              {isProfitable ? (
              <ArrowUpIcon className="h-5 w-5 text-green-500" />
              ) : (
              <ArrowDownIcon className="h-5 w-5 text-red-500" />
            )}
            <span className="text-sm text-slate-500 dark:text-slate-400">현재 가격</span>
          </div>
          <div className="text-right">
            <div className={`text-2xl font-bold ${isProfitable ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatPrice(position.currentPrice)}
            </div>
          </div>
        </div>
      </div>

      {/* 보유량 정보 */}
      <div className="bg-white dark:bg-dark-800 rounded-xl border border-slate-200 dark:border-dark-600 p-4 space-y-3">
        <div className="flex items-center space-x-2 mb-3">
          <WalletIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">보유량 정보</span>
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500 dark:text-slate-400">보유량</span>
            <span className="text-sm font-bold text-slate-900 dark:text-white">{position.size} BTC</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500 dark:text-slate-400">USDT 가치</span>
            <span className="text-sm font-medium text-slate-900 dark:text-white">
              {formatPrice(position.usdtValue)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-slate-500 dark:text-slate-400">평균 매수가</span>
            <span className="text-sm font-medium text-slate-900 dark:text-white">
              {formatPrice(position.averagePrice)}
            </span>
          </div>
          </div>
        </div>

      {/* 손익 카드 */}
      <div className={`rounded-xl border p-4 ${
        isProfitable 
          ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
      }`}>
          <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {isProfitable ? (
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            ) : (
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
            )}
            <span className="text-sm text-slate-600 dark:text-slate-400">미실현 손익</span>
          </div>
            <div className="text-right">
            <div className={`text-xl font-bold ${isProfitable ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {isProfitable ? '+' : ''}{formatPrice(position.pnl)}
              </div>
            <div className={`text-sm font-medium ${isProfitable ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {formatPercentage(position.pnlPercent)}
              </div>
            </div>
          </div>
        </div>

        {/* 거래 버튼 */}
      <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleBuy}
            onMouseEnter={() => setIsBuyHovered(true)}
            onMouseLeave={() => setIsBuyHovered(false)}
            disabled={isLoading}
            className={`
            ${isLoading 
              ? 'bg-green-300 text-green-800 cursor-not-allowed opacity-75' 
              : 'bg-green-600 hover:bg-green-700 text-white cursor-pointer shadow-md hover:shadow-lg'
            } 
            py-3 px-4 rounded-xl font-semibold 
            flex items-center justify-center space-x-2 transition-all duration-200
            `}
            style={{
              backgroundColor: isLoading 
                ? 'rgb(134 239 172)' 
                : (isBuyHovered ? 'rgb(21 128 61)' : 'rgb(22 163 74)'),
              color: isLoading ? 'rgb(22 101 52)' : 'white',
              transform: !isLoading && isBuyHovered ? 'translateY(-1px)' : 'translateY(0)',
              boxShadow: !isLoading && isBuyHovered
                ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
          <CurrencyDollarIcon className="h-4 w-4" />
          <span className="font-bold">{isLoading ? '처리 중...' : '매수'}</span>
          </button>
          <button
            onClick={handleSell}
            onMouseEnter={() => setIsSellHovered(true)}
            onMouseLeave={() => setIsSellHovered(false)}
            disabled={isLoading}
            className={`
            ${isLoading 
              ? 'bg-red-300 text-red-800 cursor-not-allowed opacity-75' 
              : 'bg-red-600 hover:bg-red-700 text-white cursor-pointer shadow-md hover:shadow-lg'
            } 
            py-3 px-4 rounded-xl font-semibold 
            flex items-center justify-center space-x-2 transition-all duration-200
            `}
            style={{
              backgroundColor: isLoading 
                ? 'rgb(252 165 165)' 
                : (isSellHovered ? 'rgb(185 28 28)' : 'rgb(220 38 38)'),
              color: isLoading ? 'rgb(153 27 27)' : 'white',
              transform: !isLoading && isSellHovered ? 'translateY(-1px)' : 'translateY(0)',
              boxShadow: !isLoading && isSellHovered
                ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
          <CurrencyDollarIcon className="h-4 w-4" />
          <span className="font-bold">{isLoading ? '처리 중...' : '매도'}</span>
          </button>
      </div>
    </div>
  );
};

export default PositionStatus; 